import { useCallback, useState } from 'react';
import { DomainError, isDomainError } from '../domain/errors';
import { useRepositories } from './useRepositories';

/** A message I sent that the conversation doesn't show yet, or that failed. */
export type OutgoingMessage = {
  /** The retry key: a retry with it can only ever store one message. */
  key: string;
  body: string;
  /** A local preview of the photo being sent. */
  photoPreview: string | null;
  state: 'sending' | 'failed';
  /** The stored message's id, once the server has it. */
  sentId: string | null;
};

type Draft = { body: string; photo: File | null; photoId: string | null };

/**
 * Optimistic sending (018 research E24): a message shows at once with a fresh
 * retry key; a failure marks it, and Retry resends with the same key. A
 * refusal (`notPermitted` — the conversation went read-only) drops it and
 * reports `'refused'` so the composer can put the text back; so does the
 * verify-first refusal, handed back as the error to show (019 R19).
 */
export function useOutbox(conversationId: string, onSettled: () => void) {
  const { conversations } = useRepositories();
  const [outgoing, setOutgoing] = useState<OutgoingMessage[]>([]);
  const [drafts] = useState(() => new Map<string, Draft>());

  const update = (key: string, change: Partial<OutgoingMessage> | null) =>
    setOutgoing((prev) =>
      change === null
        ? prev.filter((m) => m.key !== key)
        : prev.map((m) => (m.key === key ? { ...m, ...change } : m)),
    );

  const deliver = useCallback(
    async (key: string): Promise<'sent' | 'refused' | 'failed' | DomainError> => {
      const draft = drafts.get(key)!;
      update(key, { state: 'sending' });
      try {
        if (draft.photo && !draft.photoId) {
          draft.photoId = (await conversations.uploadMessagePhoto(draft.photo)).id;
        }
        const sent = await conversations.send(conversationId, {
          body: draft.body || null,
          photoIds: draft.photoId ? [draft.photoId] : [],
          idempotencyKey: key,
        });
        drafts.delete(key);
        update(key, { sentId: sent.id });
        onSettled();
        return 'sent';
      } catch (e) {
        if (isDomainError(e, 'verificationRequired')) {
          drafts.delete(key);
          update(key, null);
          return e;
        }
        if (isDomainError(e, 'notPermitted')) {
          drafts.delete(key);
          update(key, null);
          onSettled();
          return 'refused';
        }
        update(key, { state: 'failed' });
        return 'failed';
      }
    },
    [conversations, conversationId, drafts, onSettled],
  );

  const send = useCallback(
    (body: string, photo: File | null) => {
      const key = crypto.randomUUID();
      drafts.set(key, { body, photo, photoId: null });
      setOutgoing((prev) => [
        ...prev,
        {
          key,
          body,
          photoPreview: photo ? previewOf(photo) : null,
          state: 'sending',
          sentId: null,
        },
      ]);
      return deliver(key);
    },
    [deliver, drafts],
  );

  const retry = useCallback((key: string) => deliver(key), [deliver]);

  /** Forget messages the conversation now shows. */
  const settle = useCallback((shownIds: Set<string>) => {
    setOutgoing((prev) => {
      const next = prev.filter((m) => !m.sentId || !shownIds.has(m.sentId));
      return next.length === prev.length ? prev : next;
    });
  }, []);

  return { outgoing, send, retry, settle };
}

function previewOf(file: File): string | null {
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}
